import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../store/rootReducer';

import { Descriptions, Table, TableColumnsType } from 'antd';

import Radio from '../../Radio';
import Button from '../../Button';
import { IGetLastClassDate, IGetTransferableClasses } from '../../../models/campusClasses.model';
import { getLastClassDate, getTransferableClasses } from '../../../api/campusClasses.api';
import useAxios from '../../../hooks/useAxios';
import ErrorResponse from '../../ErrorResponse';
import Loader from '../../Loader';
import { IGetLessonDate } from '../../../models/transferPay.model';
import { getStartLessonDate } from '../../../api/transferPay.api';
import moment from 'moment';
import useNotification from '../../../hooks/useNotification';
import { getDayOfWeek } from '../../../lib/util';

interface IProps {
  setTab: (tab: string) => void;
  classCode: string;
  setClassCode: (classCode: string) => void;
  timetableCode: number|null|undefined;
  setTimetableCode: (timetableCode: number|null) => void;
}

const ClassTransfer: FC<IProps> = ({
  setTab,
  classCode,
  setClassCode,
  timetableCode,
  setTimetableCode,
}) => {
  const { t } = useTranslation();
  const { chosenClass } = useSelector((state: RootState) => state.class);
  const { serverDate } = useSelector((state: RootState) => state.serverDate);  
  const { infoNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetTransferableClasses>();
  const { data: res2, isLoading: isLoading2, error: error2, execute: execute2 } = useAxios<IGetLastClassDate>();
  const { data: res3, isLoading: isLoading3, error: error3, execute: execute3 } = useAxios<IGetLessonDate>();

  const [data, setData] = useState<any>([]);

  useEffect(() => {
    if (chosenClass) {
      execute(getTransferableClasses(chosenClass.classCode));
    }
  }, [chosenClass]);

  useEffect(() => {
    if (res && !isLoading) {
      const _data: any = res.data.filter(item => item.levelName === chosenClass?.courseName);
      setData(_data);
    }
  }, [res, isLoading]);

  useEffect(() => {
    if (chosenClass?.classCode) {
      execute2(getLastClassDate(chosenClass.classCode));
    }
  }, [chosenClass]);

  useEffect(() => {
    if (res2 && !isLoading2) {
      if (res2.data?.timetableCode) {
        setTimetableCode(res2.data.timetableCode);
      } else {
        setTimetableCode(null);
      }

      if (res2.data?.timetableDate) {
        execute3(getStartLessonDate(classCode, {
          inDate: moment(res2.data.timetableDate).add(1, 'days').format('YYYYMMDD'),
        }));
      } else {
        execute3(getStartLessonDate(classCode, {
          inDate: moment(serverDate).add(1, 'days').format('YYYYMMDD'),
        }));
      }
    }
  }, [res2, isLoading2]);

  useEffect(() => {
    if (res3 && !isLoading3) {
      if (!res3.data) {
        infoNotification(t('class.class.message21'));
        setClassCode('');
      }
    }
  }, [res3, isLoading3]);

  const columns: TableColumnsType<any> = [
    {
      title: '',
      dataIndex: 'classCode',
      key: 'classCode',
      align: 'center',
      render: (value: string) => (
        <Radio
          checked={value === classCode}
          onChange={() => setClassCode(value)}
          text=""
        />
      ),
    },
    {
      title: t('class.class.column51'),
      dataIndex: 'className',
      key: 'className',
      align: 'center',
    },
    {
      title: t('class.class.column52'),
      dataIndex: 'courseDetailName',
      key: 'courseDetailName',
      align: 'center',
    },
    {
      title: t('class.class.column53'),
      dataIndex: 'levelName',
      key: 'levelName',
      align: 'center',
    },
    {
      title: t('class.class.column54'),
      dataIndex: 'courseName',
      key: 'courseName',
      align: 'center',
    },
    {
      title: t('class.class.column55'),
      dataIndex: 'studentTotal',
      key: 'studentTotal',
      align: 'center',
    },
  ];

  return (
    <>
      {isLoading || isLoading2 || isLoading3 ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />
      <ErrorResponse error={error3} />

      <div className="comm-bx__between">        
        <h3 className="sub-title">{chosenClass?.className}</h3>
      </div>
      <div className="comm-table__area">
        <Table
          columns={columns}
          dataSource={data}
          pagination={{
            pageSize: 99999
          }}
          rowKey="classCode"
        />
      </div>
      <div className="comm-table__area rowType mt-5">
        <Descriptions
          bordered
          column={1}
          size="small"
          labelStyle={{ width: '25%', textAlign: 'center' }}
        >
          <Descriptions.Item label={t('class.class.column56')}>
            {
              res2?.data?.timetableDate
                ? (
                  <>
                    {moment(res2.data.timetableDate).format('YYYY.MM.DD')}
                    ({getDayOfWeek(res2.data.timetableDate)}) 
                  </>
                  )
                : res2?.data
                  ? t('class.class.text18')
                  : '-'
            }
          </Descriptions.Item>
        </Descriptions>
      </div>
      <div className="comm-bx__between">
        <div className="comm-btn__area">
          <Button
            className="btn-basic gray-line"
            onClick={() => setTab('register')}
            text={t('common.button.back')}
          />
        </div>        
        <div className="comm-btn__area">
          <Button
            className="btn-basic pink-line"
            onClick={() => setTab('transferCalculation')}
            text={t('common.button.next')}
            disabled={!classCode}
          />
        </div>
      </div>
    </>
  );
}

export default ClassTransfer;
